/* Somewear — product page: gallery, lightbox, variant picker, product form, recommendations. */
(() => {
  const S = window.Somewear;

  /* ---------------------------------------------------------------------
     Gallery
     --------------------------------------------------------------------- */
  class ProductGallery extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-gallery-track]');
      this.slides = [...this.querySelectorAll('.gallery__slide')];
      this.thumbs = [...this.querySelectorAll('[data-thumb]')];
      this.counter = this.querySelector('[data-gallery-current]');
      this.lightbox = this.querySelector('[data-lightbox]');
      this.current = 0;

      this.thumbs.forEach((thumb) =>
        thumb.addEventListener('click', () => this.goTo(parseInt(thumb.dataset.thumb, 10)))
      );
      const prev = this.querySelector('[data-gallery-prev]');
      const next = this.querySelector('[data-gallery-next]');
      if (prev) prev.addEventListener('click', () => this.goTo(this.current - 1));
      if (next) next.addEventListener('click', () => this.goTo(this.current + 1));

      if ('IntersectionObserver' in window && this.slides.length > 1) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) this.setActive(parseInt(entry.target.dataset.index, 10));
            });
          },
          { root: this.track, threshold: 0.6 }
        );
        this.slides.forEach((slide) => this.observer.observe(slide));
      }

      this.addEventListener('click', (event) => {
        const opener = event.target.closest('[data-lightbox-open]');
        if (opener) this.openLightbox(parseInt(opener.dataset.lightboxOpen, 10));
      });

      if (this.lightbox) {
        this.lightbox.querySelector('[data-lightbox-close]').addEventListener('click', () => this.lightbox.close());
        this.lightbox.addEventListener('close', () => {
          document.body.classList.remove('is-locked');
          const opener = this.querySelector(`[data-lightbox-open="${this.current}"]`);
          if (opener) opener.focus({ preventScroll: true });
        });
        this.lightbox.addEventListener('click', (event) => {
          if (event.target.closest('.lightbox__item img')) this.lightbox.close();
        });
      }
    }

    goTo(index) {
      const total = this.slides.length;
      if (!total) return;
      const target = (index + total) % total;
      const slide = this.slides[target];
      this.track.scrollTo({
        left: slide.offsetLeft, // .gallery__track is position: relative
        behavior: S.reduceMotion() ? 'auto' : 'smooth',
      });
      this.setActive(target);
    }

    goToMedia(mediaId) {
      const slide = this.slides.find((s) => s.dataset.mediaId === String(mediaId));
      if (slide) this.goTo(parseInt(slide.dataset.index, 10));
    }

    setActive(index) {
      if (Number.isNaN(index)) return;
      this.current = index;
      this.thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === index);
        if (i === index) thumb.setAttribute('aria-current', 'true');
        else thumb.removeAttribute('aria-current');
      });
      if (this.counter) this.counter.textContent = String(index + 1).padStart(2, '0');
      // Pause any video that scrolled out of view.
      this.slides.forEach((slide, i) => {
        if (i === index) return;
        const video = slide.querySelector('video');
        if (video && !video.paused) video.pause();
      });
    }

    openLightbox(index) {
      if (!this.lightbox || !this.lightbox.showModal) return;
      this.current = index;
      this.lightbox.showModal();
      document.body.classList.add('is-locked');
      const item = this.lightbox.querySelector(`[data-lightbox-index="${index}"]`);
      const img = item && item.querySelector('img');
      if (img) img.loading = 'eager';
      if (item) item.scrollIntoView({ block: 'start' });
      this.lightbox.querySelector('[data-lightbox-scroller]').focus({ preventScroll: true });
    }
  }
  customElements.define('product-gallery', ProductGallery);

  /* ---------------------------------------------------------------------
     Variant picker
     --------------------------------------------------------------------- */
  class VariantPicker extends HTMLElement {
    connectedCallback() {
      const json = this.querySelector('[data-variants]');
      this.variants = json ? JSON.parse(json.textContent) : [];
      this.page = this.closest('product-page');
      this.form = document.getElementById(this.dataset.formId);
      this.inputs = [...this.querySelectorAll('.variants__input')];
      this.optionCount = this.querySelectorAll('fieldset').length;

      this.addEventListener('change', () => this.onChange());
      this.updateAvailability();
    }

    selectedOptions() {
      const selected = [];
      this.inputs.forEach((input) => {
        if (input.checked) selected[parseInt(input.dataset.optionIndex, 10)] = input.value;
      });
      return selected;
    }

    onChange() {
      const options = this.selectedOptions();
      const variant = this.variants.find((v) => v.options.every((value, i) => value === options[i]));

      options.forEach((value, i) => {
        const label = this.querySelector(`[data-option-value="${i}"]`);
        if (label) label.textContent = value;
      });

      this.updateAvailability();
      this.updatePage(variant);
    }

    updateAvailability() {
      const options = this.selectedOptions();
      this.inputs.forEach((input) => {
        const index = parseInt(input.dataset.optionIndex, 10);
        const available = this.variants.some(
          (v) =>
            v.available &&
            v.options[index] === input.value &&
            v.options.every((value, i) => i === index || value === options[i])
        );
        const label = this.querySelector(`label[for="${CSS.escape(input.id)}"]`);
        if (label) label.classList.toggle('is-unavailable', !available);
      });
    }

    updatePage(variant) {
      const page = this.page || document;
      const addButton = this.form && this.form.querySelector('[data-add-button]');
      const addLabel = addButton && addButton.querySelector('[data-add-label]');
      const idInput = this.form && this.form.querySelector('[data-variant-id]');
      const priceEl = page.querySelector('[data-product-price] .price');
      const lowStock = page.querySelector('[data-low-stock]');

      if (!variant) {
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        if (lowStock) lowStock.hidden = true;
        return;
      }

      if (idInput) {
        idInput.value = variant.id;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }

      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';

      if (priceEl) {
        priceEl.classList.toggle('price--sale', Boolean(variant.compare));
        priceEl.innerHTML =
          `<span class="price__current">${variant.price}</span>` +
          (variant.compare ? `<s class="price__compare"><span class="sr-only">Regular price</span>${variant.compare}</s>` : '');
      }

      if (lowStock) {
        const threshold = parseInt(lowStock.dataset.threshold, 10);
        const show = variant.available && variant.stock !== null && variant.stock > 0 && variant.stock <= threshold;
        lowStock.hidden = !show;
        if (show) lowStock.querySelector('[data-low-stock-count]').textContent = variant.stock;
      }

      if (variant.media) {
        const gallery = page.querySelector('product-gallery');
        if (gallery && gallery.goToMedia) gallery.goToMedia(variant.media);
      }

      if (this.page && this.page.dataset.url) {
        window.history.replaceState({}, '', `${this.page.dataset.url}?variant=${variant.id}`);
      }
    }
  }
  customElements.define('variant-picker', VariantPicker);

  /* ---------------------------------------------------------------------
     Product form: AJAX add to cart -> open cart drawer
     --------------------------------------------------------------------- */
  class ProductForm extends HTMLElement {
    connectedCallback() {
      this.form = this.querySelector('form');
      this.button = this.querySelector('[data-add-button]');
      this.error = this.querySelector('[data-form-error]');
      this.form.addEventListener('submit', (event) => this.onSubmit(event));

      this.addEventListener('click', (event) => {
        const stepper = event.target.closest('[data-qty-change]');
        if (!stepper) return;
        const input = stepper.closest('[data-qty]').querySelector('input');
        const next = (parseInt(input.value, 10) || 1) + parseInt(stepper.dataset.qtyChange, 10);
        input.value = Math.max(1, next);
      });
    }

    async onSubmit(event) {
      if (!S.cart) return; // Falls back to a normal form post.
      event.preventDefault();
      if (this.button.disabled || this.button.classList.contains('is-loading')) return;

      this.error.hidden = true;
      this.button.classList.add('is-loading');
      this.button.setAttribute('aria-busy', 'true');

      try {
        await S.cart.add(new FormData(this.form));
        S.announce('Added to cart.');
        S.openCart(this.button);
      } catch (error) {
        this.error.textContent = error.message;
        this.error.hidden = false;
      } finally {
        this.button.classList.remove('is-loading');
        this.button.removeAttribute('aria-busy');
      }
    }
  }
  customElements.define('product-form', ProductForm);

  /* ---------------------------------------------------------------------
     Recommendations, loaded when the section approaches the viewport
     --------------------------------------------------------------------- */
  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.children.length || !this.dataset.url) return;
      const load = async () => {
        try {
          const response = await fetch(this.dataset.url);
          const doc = S.parseHTML(await response.text());
          const incoming = doc.querySelector('product-recommendations');
          if (incoming && incoming.innerHTML.trim()) {
            this.innerHTML = incoming.innerHTML;
            S.observeReveals(this);
          } else {
            this.remove();
          }
        } catch (error) {
          this.remove();
        }
      };
      if (!('IntersectionObserver' in window)) return load();
      const observer = new IntersectionObserver(
        (entries) => {
          if (!entries[0].isIntersecting) return;
          observer.disconnect();
          load();
        },
        { rootMargin: '0px 0px 400px 0px' }
      );
      observer.observe(this);
    }
  }
  customElements.define('product-recommendations', ProductRecommendations);
})();
